import { axiosRubyClient } from '@/util/axios/axiosClient';

export interface OnboardingStep {
  id: string;
  title: string;
  status: 'pending' | 'in_progress' | 'completed' | 'under_review' | 'rejected';
  disabled: boolean;
}

export interface GetOnboardingStepsResponse {
  success: boolean;
  steps: OnboardingStep[];
  message?: string;
}

export interface UpdateStepRequest {
  id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'under_review' | 'rejected';
}

export interface UpdateStepsResponse {
  success: boolean;
  steps: OnboardingStep[];
  message?: string;
}

export interface AuditLogEntry {
  id: number;
  step_id: string;
  step_title: string;
  action: string;
  column_name: string;
  change_summary: string;
  old_value: string | null;
  new_value: string | null;
  changed_at: string;
  changed_by: {
    type: string;
    name: string;
  };
  request_context: {
    request_id: string;
    ip_address: string;
    user_agent: string;
  };
}

export interface AuditLogsPagination {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface GetAuditLogsResponse {
  success: boolean;
  data: {
    worker_id: number;
    worker_name: string;
    // Every matching row, not only this page's.
    total_logs: number;
    audit_logs: AuditLogEntry[];
  };
  // Absent from a graphy that does not page this route yet (one page).
  pagination?: AuditLogsPagination;
}

export interface AuditLogsPaging {
  page?: number;
  per_page?: number;
}

export class OnboardingStepsAPI {
  /**
   * Get onboarding steps for a worker
   */
  static async getWorkerOnboardingSteps(
    workerId: number,
  ): Promise<GetOnboardingStepsResponse> {
    const response = await axiosRubyClient.get(
      `/api/admin/onboarding_steps?worker_id=${workerId}`,
    );
    return response.data;
  }

  /**
   * Update worker onboarding step status
   */
  static async updateWorkerOnboardingSteps(
    workerId: number,
    stepId: string,
    status: string,
    note?: string,
  ): Promise<UpdateStepsResponse> {
    const response = await axiosRubyClient.patch(
      `/api/admin/onboarding_steps`,
      {
        worker_id: workerId,
        step_id: stepId,
        status,
        note,
      },
    );
    return response.data;
  }

  /**
   * Get audit logs for worker onboarding steps
   */
  static async getAuditLogs(
    workerId: number,
    stepId?: string,
    columnName?: string,
    paging: AuditLogsPaging = {},
  ): Promise<GetAuditLogsResponse> {
    const params = new URLSearchParams();
    params.append('worker_id', workerId.toString());
    if (stepId) params.append('step_id', stepId);
    if (columnName) params.append('column_name', columnName);
    if (paging.page) params.append('page', paging.page.toString());
    if (paging.per_page) params.append('per_page', paging.per_page.toString());

    const response = await axiosRubyClient.get(
      `/api/admin/onboarding_step_audit_logs?${params.toString()}`,
    );
    return response.data;
  }
}
